import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Target, RefreshCw, ChevronRight, AlertTriangle } from 'lucide-react';
import { useAuth } from '../../../context/AuthContext';
import { LineaCompacta } from './LineaProyeccion';
import useProyeccion from './useProyeccion';
import { MESES, estadoLinea, fmtCorte, fmtPct, fmtUSD, unidadDia } from './proyeccionUtils';

export function TablaVendedores({ vendedores, pocoDato, onClickVendedor }) {
  return (
    <div className="table-wrap">
      <table className="dt">
        <thead>
          <tr>
            <th>Vendedor</th>
            <th style={{ textAlign: 'right' }}>Objetivo</th>
            <th style={{ textAlign: 'right' }}>Facturado</th>
            <th style={{ textAlign: 'right' }}>Piso</th>
            <th style={{ textAlign: 'right' }}>Forecast</th>
            <th style={{ textAlign: 'right' }}>vs obj.</th>
            <th style={{ textAlign: 'right' }}>Ritmo necesario</th>
          </tr>
        </thead>
        <tbody>
          {vendedores.map(v => {
            const e = estadoLinea(v, pocoDato);
            return (
              <tr key={v.vendedor} onClick={onClickVendedor ? () => onClickVendedor(v) : undefined}
                  style={{ cursor: onClickVendedor ? 'pointer' : 'default' }}>
                <td>
                  <span style={{ display: 'inline-block', width: 8, height: 8, borderRadius: '50%', background: e.color, marginRight: 8 }} title={e.label} />
                  {v.vendedor}
                </td>
                <td style={{ textAlign: 'right' }}>{v.objetivo ? fmtUSD(v.objetivo) : '—'}</td>
                <td style={{ textAlign: 'right' }}>{fmtUSD(v.real)}</td>
                <td style={{ textAlign: 'right' }}>{fmtUSD(v.piso)}</td>
                <td style={{ textAlign: 'right' }}>{fmtUSD(v.forecast)}</td>
                <td style={{ textAlign: 'right', color: pocoDato ? 'var(--text-3)' : e.color, fontWeight: 600 }}>{fmtPct(v.forecast_vs_obj)}</td>
                <td style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>
                  {v.objetivo_alcanzado ? '✓' : v.ritmo_necesario != null ? <>{fmtUSD(v.ritmo_necesario)} <span style={{ color: 'var(--text-3)', fontSize: 11 }}>/ {unidadDia(v)}</span></> : '—'}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

export function AvisoPocoDato({ dias }) {
  if (!dias?.poco_dato) return null;
  return (
    <div style={{ display: 'flex', gap: 8, alignItems: 'center', fontSize: 12, color: 'var(--amber)', background: 'var(--amber-dim)', borderRadius: 8, padding: '8px 12px' }}>
      <AlertTriangle size={14} strokeWidth={2} style={{ flexShrink: 0 }} />
      Recién van {dias.transcurridos.toLocaleString('es-AR', { maximumFractionDigits: 1 })} días hábiles del mes: el forecast todavía se mueve mucho con cada factura.
    </div>
  );
}

export function BotonActualizar({ actualizando, onClick, corte }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
      {corte && <span style={{ fontSize: 11, color: 'var(--text-3)' }}>Datos al {fmtCorte(corte)}</span>}
      <button onClick={onClick} disabled={actualizando}
        title="Releer la facturación y los pendientes (la hoja se renueva cada 10 minutos)"
        style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 11, padding: '4px 8px', borderRadius: 6, border: '1px solid var(--border)', background: 'var(--surface-2)', color: 'var(--text-2)', cursor: 'pointer' }}>
        <RefreshCw size={12} strokeWidth={2} style={{ animation: actualizando ? 'spin .7s linear infinite' : 'none' }} />
        {actualizando ? 'Actualizando' : 'Actualizar'}
      </button>
    </div>
  );
}

/** Proyección del mes en curso, arriba del podio del dashboard. Compacta a propósito:
 *  junto con el podio y el top de clientes tiene que entrar en la primera pantalla.
 *  El desglose por vendedor vive solo en "Ver detalle". */
export default function ProyeccionWidget() {
  const navigate = useNavigate();
  const { modules, effectiveRole: role } = useAuth();
  const { data, loading, actualizando, actualizar } = useProyeccion();
  // Admin con objetivo comercial propio: arranca en su mes y puede pasar a la general.
  const [vista, setVista] = useState('propia');

  // Sin acceso (ni vendedor asociado ni rol de vista general): no se muestra nada.
  if (loading && !data) return null;
  if (!data || !data.visible) return null;

  const tieneCartera = role === 'dev' || modules.some(m => m.id === 'cartera' && m.active && m.roles.includes(role));
  const generalData = data.total ? { ...data.total, dias: data.dias } : null;
  // Un admin con vendedor pero sin objetivo este mes ve solo la general.
  const conToggle = !!generalData && data.propia?.objetivo > 0;
  const propia = generalData && !conToggle ? null : data.propia;
  const general = conToggle && vista === 'propia' ? null : generalData;
  const mostrarPropia = conToggle ? vista === 'propia' : !!propia;

  return (
    <div className="card" style={{ marginBottom: 16, padding: '16px 20px', opacity: actualizando ? 0.6 : 1, transition: 'opacity .2s' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, flexWrap: 'wrap', marginBottom: 12 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <Target size={16} strokeWidth={1.75} color="var(--accent)" />
          <span style={{ fontSize: 15, fontWeight: 600 }}>Proyección de {MESES[data.mes - 1]}</span>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
          {conToggle && (
            <div style={{ display: 'flex', background: 'var(--surface-2)', borderRadius: 8, padding: 2, gap: 2 }}>
              {[['propia', 'Mi mes'], ['general', 'General']].map(([id, label]) => (
                <button key={id} onClick={() => setVista(id)}
                  style={{
                    padding: '4px 12px', borderRadius: 6, border: 'none', fontSize: 12, fontWeight: 500, cursor: 'pointer',
                    background: vista === id ? 'var(--accent)' : 'transparent',
                    color: vista === id ? 'white' : 'var(--text-2)', transition: 'background .15s, color .15s',
                  }}>
                  {label}
                </button>
              ))}
            </div>
          )}
          <BotonActualizar actualizando={actualizando} onClick={actualizar} corte={data.corte} />
          {tieneCartera && (
            <button className="btn btn-ghost btn-sm" onClick={() => navigate('/cartera/proyeccion')} style={{ padding: '4px 8px' }}>
              Ver detalle <ChevronRight size={14} />
            </button>
          )}
        </div>
      </div>

      {data.dias?.poco_dato && <div style={{ marginBottom: 10 }}><AvisoPocoDato dias={data.dias} /></div>}

      <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
        {general && (
          <LineaCompacta linea={general} titulo="Empresa" subtitulo="Todos los vendedores" pocoDato={data.dias?.poco_dato} propia={false} />
        )}
        {mostrarPropia && propia && (
          <LineaCompacta linea={propia} titulo="Tu mes" subtitulo={propia.vendedor} pocoDato={propia.dias?.poco_dato} />
        )}
      </div>
    </div>
  );
}
